import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Drawer,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Space,
  Switch,
  Typography,
  theme,
} from "antd";
import {
  DeleteOutlined,
  ExperimentOutlined,
  SaveOutlined,
  ThunderboltOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { useList, useTranslate } from "@refinedev/core";
import { usePageStyles } from "../../../shared/styles";
import { ConditionsBuilder } from "./ConditionsBuilder";
import { RuleTesterPanel } from "./RuleTesterPanel";
import {
  ProductRuleField,
  type IProductRule,
  type IProductRuleCondition,
  type IProductRuleMetadata,
} from "../../../models/product-rule";

const UNGROUPED_KEY = "__ungrouped__";

interface ProductRuleCardProps {
  rule: IProductRule;
  metadata?: IProductRuleMetadata;
  onUpdate: (id: string | number, values: Partial<IProductRule>) => void;
  onDelete: (id: string | number) => void;
}

interface FormValues {
  name: string;
  description?: string | null;
  message?: string | null;
  isActive: boolean;
  productIds: string[];
  conditions: IProductRuleCondition[];
}

/**
 * One rule, edited in place. Mirrors the assignment-rules card: a two-column
 * body with conditions on the left and effects on the right, a dirty-gated
 * Save, and a delete handled by the parent.
 *
 * Product rules are deny-only with no priority, so there is deliberately no
 * move-up/move-down control here.
 */
export const ProductRuleCard: React.FC<ProductRuleCardProps> = ({
  rule,
  metadata,
  onUpdate,
  onDelete,
}) => {
  const _t = useTranslate();
  const [form] = Form.useForm<FormValues>();
  const { token } = theme.useToken();
  const pageStyles = usePageStyles();
  const [isDirty, setIsDirty] = useState(false);
  const [testerOpen, setTesterOpen] = useState(false);

  const conditions = Form.useWatch("conditions", form) ?? [];

  const { data: productsData, isLoading: productsLoading } = useList({
    resource: "products",
    pagination: { mode: "off" },
  });

  /** Grouped `group · type` options, matching the assignment-rules card. */
  const productOptions = useMemo(() => {
    const products = (productsData?.data ?? []) as Array<Record<string, any>>;
    const buckets = new Map<
      string,
      { group: string; type: string; leaves: { value: string; label: string }[] }
    >();

    for (const product of products) {
      const group = typeof product.group === "string" ? product.group.trim() : "";
      const type = typeof product.type === "string" ? product.type : "";
      const name = product.name || String(product.id);
      const key = `${group || UNGROUPED_KEY}||${type}`;
      const leaf = { value: String(product.id), label: name };
      const bucket = buckets.get(key);
      if (bucket) bucket.leaves.push(leaf);
      else buckets.set(key, { group, type, leaves: [leaf] });
    }

    const typeOrder = (type: string) =>
      type === "base" ? 0 : type === "option" ? 1 : 2;

    return [...buckets.values()]
      .sort((a, b) => {
        const groupCompare = (a.group || "￿").localeCompare(
          b.group || "￿",
          undefined,
          { sensitivity: "base" },
        );
        return groupCompare !== 0
          ? groupCompare
          : typeOrder(a.type) - typeOrder(b.type);
      })
      .map((bucket) => {
        const groupLabel =
          bucket.group || _t("service-providers.productMapping.ungrouped", "Other");
        const typeLabel = bucket.type
          ? _t(`products.fields.typeValues.${bucket.type}`, bucket.type)
          : "";
        return {
          label: typeLabel ? `${groupLabel} · ${typeLabel}` : groupLabel,
          options: bucket.leaves.sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
          ),
        };
      });
  }, [productsData, _t]);

  /** The API returns hydrated products but accepts numeric ids on write. */
  const normalizedProductIds = useMemo(() => {
    if (Array.isArray(rule.products) && rule.products.length > 0) {
      return rule.products.map((product) => String(product.id));
    }
    return (rule.productIds ?? []).map((id) => String(id));
  }, [rule.products, rule.productIds]);

  const resetForm = () => {
    form.setFieldsValue({
      name: rule.name,
      description: rule.description ?? null,
      message: rule.message ?? null,
      isActive: rule.isActive ?? true,
      productIds: normalizedProductIds,
      conditions: (rule.conditions ?? []).map((condition) => ({ ...condition })),
    });
    setIsDirty(rule._isNew === true);
  };

  useEffect(resetForm, [rule, form]);

  const handleValuesChange = (changed: Partial<FormValues>) => {
    setIsDirty(true);

    // Changing a row's field invalidates its operator and values: the new field
    // may not accept the old operator, and the old values belong to a different
    // vocabulary entirely.
    if (changed.conditions) {
      const next = form.getFieldValue("conditions") as IProductRuleCondition[];
      const previous = (rule.conditions ?? []) as IProductRuleCondition[];
      let mutated = false;
      const reconciled = next.map((condition, index) => {
        const changedRow = (changed.conditions as unknown[])?.[index] as
          | Partial<IProductRuleCondition>
          | undefined;
        if (changedRow?.field && changedRow.field !== previous[index]?.field) {
          const allowed =
            metadata?.fields.find((field) => field.key === changedRow.field)
              ?.operators ?? [];
          if (condition.operator && !allowed.includes(condition.operator)) {
            mutated = true;
            return { ...condition, operator: undefined as never, values: [] };
          }
        }
        return condition;
      });
      if (mutated) {
        form.setFieldsValue({ conditions: reconciled });
      }
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      onUpdate(rule.id, {
        name: values.name,
        description: values.description || null,
        message: values.message || null,
        isActive: values.isActive,
        productIds: (values.productIds ?? []).map((id) => Number(id)),
        conditions: values.conditions ?? [],
      });
      setIsDirty(false);
    } catch {
      // antd has already surfaced the per-field errors.
    }
  };

  const draftForTester = () => {
    const values = form.getFieldsValue();
    return {
      // A saved rule is masked by its own draft so the drawer previews the edit
      // rather than evaluating the stored version alongside it.
      ruleId: rule._isNew ? undefined : Number(rule.id),
      name: values.name || _t("product-rules.fields.newRule", "New rule"),
      message: values.message ?? null,
      productIds: (values.productIds ?? []).map((id) => Number(id)),
      conditions: (values.conditions ?? []).filter(
        (condition) => condition?.field && condition?.operator,
      ),
    };
  };

  return (
    <Card
      style={pageStyles.stepperCard}
      title={
        <Flex gap={12} align="center" wrap="wrap">
          <Typography.Text strong>
            {rule.name || _t("product-rules.fields.newRule", "New rule")}
          </Typography.Text>
          {rule._isNew && (
            <Typography.Text type="secondary" className="text-xs">
              {_t("product-rules.fields.unsaved", "Unsaved")}
            </Typography.Text>
          )}
        </Flex>
      }
      actions={[
        <Flex key="actions" justify="end" gap={8} className="px-4">
          <Button icon={<UndoOutlined />} onClick={resetForm} disabled={!isDirty}>
            {_t("buttons.reset", "Reset")}
          </Button>
          <Button
            icon={<ExperimentOutlined />}
            onClick={() => setTesterOpen(true)}
          >
            {_t("product-rules.actions.test", "Test")}
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => onDelete(rule.id)}
          >
            {_t("buttons.delete", "Delete")}
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            disabled={!isDirty}
          >
            {_t("buttons.save", "Save")}
          </Button>
        </Flex>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleValuesChange}
        initialValues={{ isActive: true, conditions: [], productIds: [] }}
      >
        <Row gutter={[16, 16]} className="mb-4">
          <Col xs={24} md={12}>
            <Form.Item
              name="name"
              label={_t("product-rules.fields.name", "Name")}
              rules={[
                { required: true, message: _t("fields.errors.required", "Required") },
              ]}
              className="!mb-0"
            >
              <Input placeholder={_t("product-rules.fields.name", "Name")} />
            </Form.Item>
          </Col>
          <Col xs={16} md={8}>
            <Form.Item
              name="description"
              label={_t("product-rules.fields.description", "Description")}
              className="!mb-0"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={8} md={4}>
            <Form.Item
              name="isActive"
              label={_t("product-rules.fields.isActive", "Active")}
              valuePropName="checked"
              className="!mb-0"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <Card style={pageStyles.stepperCard} className="h-full">
              <Flex gap={8} align="center" className="mb-4">
                <ThunderboltOutlined style={{ color: token.colorPrimary }} />
                <span className="uppercase text-md font-bold">
                  {_t("product-rules.fields.conditions", "Conditions")}
                </span>
              </Flex>
              <Typography.Text type="secondary" className="mb-3 block text-sm">
                {_t(
                  "product-rules.fields.conditionsHint",
                  "All conditions must match. A field left blank on the order never matches, so nothing is hidden.",
                )}
              </Typography.Text>
              <ConditionsBuilder
                metadata={metadata}
                conditions={conditions}
                onChange={() => setIsDirty(true)}
              />
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card style={pageStyles.stepperCard} className="h-full">
              <Flex gap={8} align="center" className="mb-4">
                <ThunderboltOutlined style={{ color: token.colorPrimary }} />
                <span className="uppercase text-md font-bold">
                  {_t("product-rules.fields.effects", "Effects")}
                </span>
              </Flex>

              <Form.Item
                name="productIds"
                label={_t(
                  "product-rules.fields.productIds",
                  "Products to hide when this rule matches",
                )}
                rules={[
                  {
                    required: true,
                    message: _t("fields.errors.required", "Required"),
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  loading={productsLoading}
                  options={productOptions}
                  placeholder={_t("product-rules.fields.productIds", "Select products")}
                  className="w-full"
                  maxTagCount="responsive"
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>

              <Form.Item
                name="message"
                label={_t("product-rules.fields.message", "Reason shown to the customer")}
              >
                <Input.TextArea
                  rows={3}
                  placeholder={_t(
                    "product-rules.fields.messagePlaceholder",
                    "Not supported on this gearbox",
                  )}
                />
              </Form.Item>
            </Card>
          </Col>
        </Row>
      </Form>

      <Drawer
        title={_t("product-rules.tester.drawerTitle", "Test this rule")}
        open={testerOpen}
        onClose={() => setTesterOpen(false)}
        width={880}
        destroyOnClose
      >
        <Space direction="vertical" className="w-full">
          <Typography.Text type="secondary">
            {_t(
              "product-rules.tester.drawerHint",
              "Evaluates this rule as an unsaved draft alongside the saved ones. Nothing is persisted.",
            )}
          </Typography.Text>
          {testerOpen && (
            <RuleTesterPanel
              metadata={metadata}
              draft={draftForTester()}
              initialContext={{
                make: (conditions.find(
                  (condition) => condition?.field === ProductRuleField.make,
                )?.values ?? [])[0],
                gearbox: (conditions.find(
                  (condition) => condition?.field === ProductRuleField.gearbox,
                )?.values ?? [])[0],
              }}
            />
          )}
        </Space>
      </Drawer>
    </Card>
  );
};
